import React from 'react';
import { CookieSettingsLink, DoNotSellOrShareLink } from './ConsentBanner';

const footerColumns = [
  {
    title: 'Explore',
    links: [
      { label: 'Browse Properties', href: '/' },
      { label: 'Guides', href: '/guides' },
      { label: 'Find an Agent', href: '/agents' },
    ],
  },
  {
    title: 'For Buyers',
    links: [
      { label: 'Guides', href: '/guides' },
      { label: 'FAQ', href: '/faq' },
    ],
  },
  {
    title: 'For Agents',
    links: [
      { label: 'Pricing', href: '/pricing' },
      { label: 'List Your Property', href: '/auth?mode=signup&role=agent' },
      { label: 'Agent Login', href: '/auth' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Careers', href: '/careers' },
      { label: 'Contact', href: '/contact' },
      // SYND-01 — the page the outreach email links to. Sits with the policy
      // links rather than the "who we are" ones: a developer who finds their
      // project indexed here arrives looking for a rule, not an introduction.
      { label: 'How listings work', href: '/syndication' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Delete account', href: '/delete-account' },
    ],
  },
];

/**
 * SSR-safe Footer component.
 * 4-column footer with links, social icons, and copyright, matching brand styling.
 */
export default function FooterSSR() {
  return (
    <footer className="bg-brand-bg border-t border-brand-border mt-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row gap-8 mb-8">
          {/* Brand */}
          <div className="md:w-1/4 shrink-0">
            <a href="/" className="inline-block mb-3">
              <img
                src="/afriqahome-logo.svg"
                alt="Afriqahome"
                width={140}
                height={35}
                className="h-9 w-auto"
              />
            </a>
            <p className="text-sm text-gray-500 leading-relaxed">
              East Africa's modern property marketplace. Find your next home with confidence.
            </p>
          </div>

          {/* Link Columns */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-8 flex-1">
            {footerColumns.map((column) => (
              <div key={column.title}>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">{column.title}</h4>
                <ul className="space-y-2">
                  {column.links.map((link) => (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        className="text-sm text-gray-500 hover:text-brand-primary transition-colors"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                  {/* GA4-02, then GA4-14: the re-consent surfaces, with the
                      policy links rather than in a column of their own —
                      somebody looking for them is looking for Privacy Policy
                      and finds them on the way. Both open the per-category
                      settings panel; the second is the CPRA-mandated opt-out
                      link and opens it focused on Advertising. */}
                  {column.title === 'Company' && (
                    <>
                      <li>
                        <CookieSettingsLink className="text-sm text-gray-500 hover:text-brand-primary transition-colors" />
                      </li>
                      <li>
                        <DoNotSellOrShareLink className="text-left text-sm text-gray-500 hover:text-brand-primary transition-colors" />
                      </li>
                    </>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-brand-border pt-6">
          {/* Social Icons */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <a
              href="https://instagram.com/afriqahome.official"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://facebook.com/Afriqahomes"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://linkedin.com/company/afriqahome"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>

          {/* Copyright */}
          <p className="text-sm text-gray-400 text-center">
            &copy; {new Date().getFullYear()} Afriqahome. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
